'use client';

/**
 * Brand statement descriptor (`TGDCARE*<BRAND>`).
 *
 * The brand enters the name patients see on their bank statement; Tagada, as
 * merchant of record, validates it before provisioning uses it. Staff review
 * (approve / reject with a reason) sits in the same card, visible to Tagada
 * staff only (the server enforces it too).
 */

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, CreditCard, Loader2, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useSessionClaims } from '@/hooks/use-session-claims';
import {
  useRxReviewStatementDescriptor,
  useRxSetStatementDescriptor,
  useRxStatementDescriptor,
  type RxStatementDescriptor,
} from '@/lib/api/rx';

const STATUS_CLASS: Record<RxStatementDescriptor['status'], string> = {
  none: 'text-muted-foreground',
  pending: 'border-amber-200 bg-amber-50 text-amber-700',
  approved: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  rejected: 'border-red-200 bg-red-50 text-red-700',
};

/** Same card-network rule as the server (`sanitizeRxDescriptor`), for the live preview. */
const previewSuffix = (raw: string): string =>
  raw
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export function RxStatementDescriptorCard({ storeId }: { storeId: string }) {
  const { t } = useTranslation('rx');
  const { toast } = useToast();
  const { isSuperAdmin, isAccountManager, isRxAdmin, whitelabelScope } = useSessionClaims();
  const isTagadaStaff = isSuperAdmin || isAccountManager || (isRxAdmin && !whitelabelScope);

  const query = useRxStatementDescriptor(storeId);
  const save = useRxSetStatementDescriptor();
  const review = useRxReviewStatementDescriptor();

  const data = query.data;
  const [value, setValue] = React.useState('');
  const [reason, setReason] = React.useState('');
  React.useEffect(() => {
    if (data?.descriptor) setValue(data.descriptor);
  }, [data?.descriptor]);

  if (query.isLoading || !data) return null;

  const suffix = previewSuffix(value);
  const tooLong = suffix.length > data.maxLength;
  const unchanged = suffix === (data.descriptor ?? '') && data.status !== 'rejected';

  const onSave = async () => {
    try {
      await save.mutateAsync({ storeId, descriptor: value });
      toast({ title: t('statementDescriptor.toast.saved') });
      await query.refetch();
    } catch (err) {
      toast({
        title: t('statementDescriptor.toast.failed'),
        description: err instanceof Error ? err.message : undefined,
        variant: 'destructive',
      });
    }
  };

  const onReview = async (decision: 'approve' | 'reject') => {
    try {
      await review.mutateAsync({
        storeId,
        decision,
        ...(decision === 'reject' ? { reason: reason.trim() } : {}),
      });
      toast({
        title:
          decision === 'approve'
            ? t('statementDescriptor.toast.approved')
            : t('statementDescriptor.toast.rejected'),
      });
      setReason('');
      await query.refetch();
    } catch (err) {
      toast({
        title: t('statementDescriptor.toast.failed'),
        description: err instanceof Error ? err.message : undefined,
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            {t('statementDescriptor.title')}
          </CardTitle>
          <Badge variant="outline" className={`${STATUS_CLASS[data.status]} font-medium`}>
            {t(`statementDescriptor.status.${data.status}`)}
          </Badge>
        </div>
        <CardDescription>{t('statementDescriptor.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="rx-statement-descriptor">{t('statementDescriptor.label')}</Label>
          <div className="flex flex-wrap gap-2">
            <div className="flex max-w-sm flex-1 items-center rounded-md border">
              <span className="text-muted-foreground bg-muted rounded-l-md px-2 py-2 font-mono text-sm">
                {data.prefix}
              </span>
              <Input
                id="rx-statement-descriptor"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={t('statementDescriptor.placeholder')}
                className="border-0 font-mono uppercase shadow-none focus-visible:ring-0"
                maxLength={40}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              disabled={!suffix || tooLong || unchanged || save.isPending}
              onClick={() => void onSave()}
            >
              {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('statementDescriptor.save')}
            </Button>
          </div>
          <p className={`text-xs ${tooLong ? 'text-red-600' : 'text-muted-foreground'}`}>
            {t('statementDescriptor.help', { max: data.maxLength, prefix: data.prefix })}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">{t('statementDescriptor.preview')}</span>
          <code className="bg-muted rounded px-2 py-1">
            {suffix ? `${data.prefix}${suffix}`.slice(0, data.prefix.length + data.maxLength) : data.fallback}
          </code>
        </div>
        {data.status !== 'approved' ? (
          <p className="text-muted-foreground text-xs">
            {t('statementDescriptor.fallback', { fallback: data.fallback })}
          </p>
        ) : null}
        {data.status === 'rejected' && data.rejectionReason ? (
          <p className="text-xs text-red-700">
            {t('statementDescriptor.rejectedReason', { reason: data.rejectionReason })}
          </p>
        ) : null}

        {isTagadaStaff && data.status === 'pending' ? (
          <div className="space-y-2 rounded-lg border border-dashed p-3">
            <p className="text-sm font-medium">{t('statementDescriptor.staff.title')}</p>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('statementDescriptor.staff.reasonPlaceholder')}
              className="max-w-md"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                disabled={review.isPending}
                onClick={() => void onReview('approve')}
              >
                <Check className="mr-1.5 h-3.5 w-3.5" />
                {t('statementDescriptor.staff.approve')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={review.isPending || reason.trim().length < 3}
                onClick={() => void onReview('reject')}
              >
                <X className="mr-1.5 h-3.5 w-3.5" />
                {t('statementDescriptor.staff.reject')}
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
